import { cn } from '@/lib/cn';
import type { ReactNode } from 'react';

export type SegmentedSwitchOption<T extends string> = {
  label: string;
  value: T;
  /** Optional leading icon, purely decorative. */
  icon?: ReactNode;
};

type Props<T extends string> = {
  value?: T;
  onChange?: (value: T) => void;
  options: SegmentedSwitchOption<T>[];
  disabled?: boolean;
  size?: 'middle' | 'large';
  className?: string;
};

/**
 * Minimal view switch: the selected option carries a soft lift, the rest stay
 * transparent. Nothing is bolded, so switching never shifts the layout.
 */
const SegmentedSwitch = <T extends string>({
  value,
  onChange,
  options,
  disabled,
  size = 'middle',
  className,
}: Props<T>) => (
  <div
    className={cn(
      'inline-flex gap-1 p-1 rounded-lg bg-theme-slate-900 border border-theme-gray-800',
      disabled && 'opacity-60',
      className,
    )}
  >
    {options.map((option) => {
      const isActive = value === option.value;
      return (
        <button
          key={option.value}
          type="button"
          disabled={disabled}
          onClick={() => onChange?.(option.value)}
          className={cn(
            'inline-flex items-center gap-2 rounded-md transition-colors whitespace-nowrap',
            size === 'large' ? 'h-9 px-4 text-sm' : 'h-8 px-3 text-sm',
            isActive
              ? 'bg-white/10 text-white'
              : 'bg-transparent text-white/45',
            !isActive && !disabled && 'hover:text-white/80',
            disabled && 'cursor-not-allowed',
          )}
        >
          {option.icon}
          {option.label}
        </button>
      );
    })}
  </div>
);

export default SegmentedSwitch;
